import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, FormGroup, Typography } from "@mui/material";
import axios from "axios";
import Logo from "../components/Logo";
import config from "../config";
import themeColors from "../theme";

export default function UnsubscribePage() {
    const [searchParams] = useSearchParams();
    const userId = searchParams.get("u");
    const token = searchParams.get("t");
    const [categories, setCategories] = useState([]);
    const [unsubscribed, setUnsubscribed] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [retry, setRetry] = useState(0);
    const endpoint = `${config.SERVER_URL}/api/email-preferences`;

    useEffect(() => {
        let active = true;
        setLoading(true);
        setCategories([]);
        setError("");
        setSuccess("");
        if (!userId || !token) {
            setError("Please open the unsubscribe link at the bottom of one of our emails to manage your preferences.");
            setLoading(false);
            return;
        }
        axios.get(endpoint, { params: { u: userId, t: token } })
            .then(({ data }) => {
                if (!active) return;
                setCategories(data.categories);
                setUnsubscribed(data.unsubscribed);
            })
            .catch((err) => {
                if (active) setError(err.response?.data?.error || "Unable to load your preferences. Please try again.");
            })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [endpoint, userId, token, retry]);

    const allSelected = categories.length > 0 && categories.every(({ key }) => unsubscribed[key]);
    const someSelected = categories.some(({ key }) => unsubscribed[key]);
    const changeSelection = (next) => {
        setUnsubscribed(next);
        setSuccess("");
        setError("");
    };

    const savePreferences = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const { data } = await axios.post(endpoint, { unsubscribed }, { params: { u: userId, t: token } });
            setUnsubscribed(data.unsubscribed);
            setSuccess(allSelected
                ? "You've unsubscribed from all periodic emails."
                : "Your preferences have been saved. You won't receive emails from the selected categories.");
        } catch (err) {
            setError(err.response?.data?.error || "Unable to save your preferences. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box component="main" sx={{ minHeight: "100vh", bgcolor: themeColors.lightGray, px: 2, py: { xs: 5, sm: 8 }, boxSizing: "border-box" }}>
            <Box sx={{ textAlign: "center", mb: 4 }}><Logo /></Box>
            <Box sx={{ maxWidth: 520, mx: "auto", bgcolor: "white", p: { xs: 3, sm: 5 }, borderRadius: 3, boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
                <Typography component="h1" variant="h5" fontWeight={700} sx={{ color: "#1D3557", mb: 1 }}>
                    Email preferences
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                    Select the emails you would like to unsubscribe from.
                </Typography>
                {loading && <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress aria-label="Loading email preferences" sx={{ color: themeColors.teal }} /></Box>}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                {!loading && categories.length > 0 && (
                    <Box component="form" onSubmit={savePreferences}>
                        <FormGroup aria-label="Unsubscribe from email categories">
                            {categories.map(({ key, label }) => (
                                <FormControlLabel key={key} label={label} sx={{ py: 0.5 }} control={
                                    <Checkbox checked={Boolean(unsubscribed[key])} disabled={saving}
                                        sx={{ "&.Mui-checked": { color: themeColors.teal } }}
                                        onChange={(event) => changeSelection({ ...unsubscribed, [key]: event.target.checked })} />
                                } />
                            ))}
                            <Divider sx={{ my: 2 }} />
                            <FormControlLabel label="Unsubscribe from all" control={
                                <Checkbox checked={allSelected} indeterminate={someSelected && !allSelected} disabled={saving}
                                    sx={{ "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: themeColors.teal } }}
                                    onChange={(event) => changeSelection(Object.fromEntries(categories.map(({ key }) => [key, event.target.checked])))} />
                            } />
                        </FormGroup>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Uncheck a category to receive those emails again. Account emails, such as password resets and email verification, will still be sent.
                        </Typography>
                        <Button type="submit" fullWidth variant="contained" disabled={saving} sx={{ mt: 3, py: 1.4, textTransform: "none", fontWeight: 600, bgcolor: themeColors.teal, "&:hover": { bgcolor: themeColors.darkTeal } }}>
                            {saving ? "Saving…" : "Save preferences"}
                        </Button>
                    </Box>
                )}
                {!loading && error && categories.length === 0 && userId && token && (
                    <Button onClick={() => setRetry((value) => value + 1)}>Try again</Button>
                )}
            </Box>
        </Box>
    );
}
